import { forwardRef, useMemo, useRef } from 'react';
import c from 'classnames';
import size from 'lodash/size';
import { Resizable, ResizeCallback } from 're-resizable';

import { useReactNodeView } from '@lyearn/lygo-core';
import { Box } from '@lyearn/ui';

import { calMaxWidth } from '../ResizeContainer/utils';
import {
  heightHandlerStyles,
  NODE_CONTAINER_CLASSNAME,
  RESIZE_BOTTOM,
  RESIZE_LEFT,
  RESIZE_RIGHT,
  RESIZE_TOP,
  resizeContainer,
  RESIZER_COMMON_CLASSES,
  widthHandlerStyles,
} from './styles';

const enabledResizingSides = {
  top: false,
  right: true,
  bottom: true,
  left: true,
  topRight: false,
  bottomRight: false,
  bottomLeft: false,
  topLeft: false,
};

const ResizeContainerV2 = forwardRef<
  any,
  React.ComponentProps<typeof Resizable> & {
    maxHeight?: number;
    wrapperProps?: React.ComponentProps<typeof Box>;
  }
>(({ children, maxHeight, wrapperProps, className, enable, ...rndProps }, ref) => {
  const { updateAttrs } = useReactNodeView();
  const refChild = useRef<HTMLDivElement>(null);

  const onResizeStop: ResizeCallback = (e, dir, refToElement, delta) => {
    const height = parseFloat(refToElement.style.height);
    const widthInPercentage = (parseFloat(refToElement.style.width) / calMaxWidth()) * 100;

    const attrs: Record<string, number | null> = {};
    if (!isNaN(height)) {
      attrs.height = height;
    }

    if (!isNaN(widthInPercentage)) {
      attrs.width = widthInPercentage;
    }

    size(attrs) && updateAttrs(attrs);
  };

  const enableResizingMemo = useMemo(
    () => ({ ...enabledResizingSides, ...(enable as object) }),
    [enable],
  );

  return (
    <Resizable
      ref={ref}
      className={c(resizeContainer, NODE_CONTAINER_CLASSNAME, className)}
      enable={enableResizingMemo}
      handleClasses={{ left: RESIZE_LEFT, right: RESIZE_RIGHT, bottom: RESIZE_BOTTOM }}
      onResizeStop={onResizeStop}
      {...rndProps}>
      <Box ref={refChild} className="h-full w-full" {...wrapperProps}>
        {children}
      </Box>
      <Box className="resize-handles hidden">
        {enableResizingMemo.top ? (
          <div
            className={c(RESIZE_TOP, RESIZER_COMMON_CLASSES, 'top-0 h-6 w-36', heightHandlerStyles)}
          />
        ) : null}
        {enableResizingMemo.bottom ? (
          <div
            className={c(
              RESIZE_BOTTOM,
              RESIZER_COMMON_CLASSES,
              'bottom-0 h-6 w-36',
              heightHandlerStyles,
            )}
          />
        ) : null}
        <div
          className={c(RESIZE_LEFT, RESIZER_COMMON_CLASSES, 'h-36 w-6 start-0', widthHandlerStyles)}
        />
        <div
          className={c(RESIZE_RIGHT, RESIZER_COMMON_CLASSES, 'h-36 w-6 end-0', widthHandlerStyles)}
        />
      </Box>
    </Resizable>
  );
});

export default ResizeContainerV2;
